import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function VotesPage() {
  const { user, profile } = useAuth();
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('trending');
  const [topics, setTopics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // SEO Configuration
  useSEO({
    title: 'Tüm Konular - Haklı Haksız | Çift Tartışmaları ve Topluluk Oylaması',
    description: 'Çiftler arasındaki tartışmalara göz atın, oy verin ve yorum yapın. İlişki, finans, ev işleri, sosyal hayat ve aile konularında topluluk kararlarını görün.',
    keywords: 'çift tartışmaları, oy verme, topluluk kararları, ilişki sorunları, finans tartışmaları, ev işleri anlaşmazlıkları',
    canonical: '/votes',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Tüm Konular - haklıhaksız.net',
          'Topluluk tarafından paylaşılan tartışmalara oy ver, yorum yap',
          '/votes'
        ),
        generateBreadcrumbSchema([
          { name: 'Ana Sayfa', url: '/' },
          { name: 'Tüm Konular', url: '/votes' }
        ]),
        {
          '@type': 'CollectionPage',
          name: 'Tüm Konular',
          description: 'Çiftler arasındaki tartışmalar ve topluluk oylamaları'
        }
      ]
    }
  });

  useEffect(() => {
    fetchTopics();
  }, [filter, sortBy]);

  const fetchTopics = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('topics')
        .select(`
          *,
          users!topics_user_id_fkey(username),
          comments(count)
        `);

      // Filter by category
      if (filter !== 'all') {
        query = query.eq('category', getCategoryName(filter));
      }

      // Sort
      if (sortBy === 'newest') {
        query = query.order('created_at', { ascending: false });
      } else if (sortBy === 'most-voted') {
        query = query.order('votes_right', { ascending: false });
      } else if (sortBy === 'trending') {
        query = query.order('votes_right', { ascending: false });
      }

      const { data, error } = await query.limit(20);

      if (error) throw error;

      const formattedTopics = data?.map((topic: any) => {
        const totalVotes = topic.votes_right + topic.votes_wrong;
        const rightPercentage = totalVotes > 0 ? Math.round((topic.votes_right / totalVotes) * 100) : 0;
        const wrongPercentage = totalVotes > 0 ? Math.round((topic.votes_wrong / totalVotes) * 100) : 0;
        
        // Check if topic is still active (within 3 days)
        const endsAt = new Date(topic.ends_at);
        const now = new Date();
        const isActive = now < endsAt && topic.status === 'active';
        const timeLeft = isActive ? getTimeLeft(topic.ends_at) : null;

        return {
          id: topic.id,
          category: topic.category,
          title: topic.title,
          description: topic.description,
          rightVotes: rightPercentage,
          wrongVotes: wrongPercentage,
          totalVotes: totalVotes,
          comments: topic.comments?.[0]?.count || 0,
          time: getTimeAgo(topic.created_at),
          categoryColor: getCategoryColor(topic.category),
          status: isActive ? 'active' : 'closed',
          timeLeft: timeLeft,
          summary: topic.summary
        };
      }) || [];

      setTopics(formattedTopics);
    } catch (error) {
      console.error('Konular yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (id: string) => {
    const categories: { [key: string]: string } = {
      'iliski': 'İlişki',
      'ev-isleri': 'Ev İşleri',
      'finans': 'Finans',
      'sosyal': 'Sosyal Hayat',
      'aile': 'Aile'
    };
    return categories[id] || id;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'İlişki': 'bg-pink-500',
      'Finans': 'bg-emerald-500',
      'Ev İşleri': 'bg-blue-500',
      'Sosyal Hayat': 'bg-purple-500',
      'Aile': 'bg-orange-500',
      'Diğer': 'bg-gray-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffInHours = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Az önce';
    if (diffInHours < 24) return `${diffInHours} saat önce`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} gün önce`;
  };

  const getTimeLeft = (endsAt: string) => {
    const now = new Date();
    const end = new Date(endsAt);
    const diffInHours = Math.floor((end.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Son 1 saat';
    if (diffInHours < 24) return `${diffInHours} saat kaldı`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} gün kaldı`;
  };

  const categories = [
    { id: 'all', name: 'Tümü', icon: 'ri-apps-line' },
    { id: 'iliski', name: 'İlişki', icon: 'ri-heart-line' },
    { id: 'ev-isleri', name: 'Ev İşleri', icon: 'ri-home-line' },
    { id: 'finans', name: 'Finans', icon: 'ri-money-dollar-circle-line' },
    { id: 'sosyal', name: 'Sosyal Hayat', icon: 'ri-group-line' },
    { id: 'aile', name: 'Aile', icon: 'ri-parent-line' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-md shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/1dcacff907dfbea56e1f04a7f4ffdd79/131f7f20085d959b4f40e787e1603cf3.png" alt="Haklı Haksız Logo" className="h-14 w-14 object-contain" />
              <span className="text-xl font-semibold text-gray-900">haklıhaksız.net</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                Ana Sayfa
              </Link>
              {user ? (
                <>
                  <Link to="/profile" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-user-line mr-1"></i>
                    {profile?.username}
                  </Link>
                  <Link to="/submit" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                    Konu Aç
                  </Link>
                </>
              ) : (
                <Link to="/auth/login" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                  Giriş Yap
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Tüm Konular
          </h1>
          <p className="text-xl text-gray-600">
            Topluluk tarafından paylaşılan tartışmalara oy ver, yorum yap
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Category Filter */}
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Kategori</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setFilter(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      filter === cat.id
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-2`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Filter */}
            <div className="lg:w-64">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Sıralama</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-sm cursor-pointer"
              >
                <option value="trending">Trend Olanlar</option>
                <option value="newest">En Yeni</option>
                <option value="most-voted">En Çok Oy Alan</option>
              </select>
            </div>
          </div>
        </div>

        {/* Topics Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Konular yükleniyor...</p>
          </div>
        ) : topics.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-600 text-lg">Henüz konu bulunmuyor</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {topics.map((topic) => (
              <div
                key={topic.id}
                className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-gray-100"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <span className={`${topic.categoryColor} text-white px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap`}>
                    {topic.category}
                  </span>
                  {topic.status === 'closed' ? (
                    <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-lock-line"></i>
                      Kapandı
                    </span>
                  ) : topic.timeLeft && (
                    <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-time-line"></i>
                      {topic.timeLeft}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  {topic.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-6 leading-relaxed line-clamp-2">
                  {topic.description}
                </p>

                {/* Voting Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl font-bold text-emerald-600">{topic.rightVotes}%</span>
                    <span className="text-sm text-gray-500">{topic.totalVotes} oy</span>
                    <span className="text-2xl font-bold text-orange-600">{topic.wrongVotes}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                      style={{ width: `${topic.rightVotes}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">Haklı</span>
                    <span className="text-xs text-gray-500">Haksız</span>
                  </div>
                </div>

                {/* Summary for closed topics */}
                {topic.status === 'closed' && topic.summary && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm text-gray-700 font-medium">
                      <i className="ri-information-line text-blue-600 mr-2"></i>
                      {topic.summary}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-chat-3-line"></i>
                      {topic.comments}
                    </span>
                    <span className="whitespace-nowrap">{topic.time}</span>
                  </div>
                  <Link
                    to={`/topic/${topic.id}`}
                    className="bg-gray-900 text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    {topic.status === 'closed' ? 'Detayları Gör' : 'Detay & Oy Ver'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
